/**
 * ImageUpload Component
 * 
 * Drag & drop image upload with preview, matching form field styles.
 */

import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion } from 'framer-motion';
import { Upload, X, Loader2 } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export interface ImageUploadProps {
  /** Label text */
  label: string;
  /** Called when a file is selected or cleared */
  onFileChange: (file?: File) => void;
  /** URL of the uploaded image (for preview) */
  imageUrl: string;
  /** Whether upload is in progress */
  isUploading: boolean;
  /** Error message */
  error?: string;
  /** Is this field required */
  required?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  onFileChange,
  imageUrl,
  isUploading,
  error,
  required,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles && acceptedFiles[0]) {
        onFileChange(acceptedFiles[0]);
      }
    },
    [onFileChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
    multiple: false,
  });

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-[#c00035] ml-1">*</span>}
      </label>

      <div
        {...getRootProps()}
        className={`
          relative flex flex-col items-center justify-center
          w-full px-6 py-8 rounded-xl
          border-2 border-dashed
          transition-all duration-200 cursor-pointer
          ${isDragActive
            ? 'border-purple-500 bg-purple-50'
            : error
              ? 'border-red-300 bg-red-50'
              : 'border-gray-200 bg-white hover:border-purple-400 hover:bg-purple-50/50'
          }
        `}
      >
        <input {...getInputProps()} />

        {/* Empty state */}
        {!imageUrl && !isUploading && (
          <>
            <Upload
              size={32}
              className={`mb-2 ${isDragActive ? 'text-purple-500' : 'text-gray-400'}`}
            />
            <p className="text-center text-sm text-gray-600">
              {isDragActive
                ? 'Drop the image here…'
                : 'Drag & drop an image, or click to browse'}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              PNG, JPG, JPEG or WEBP • max 5 MB
            </p>
          </>
        )}

        {/* Uploading state */}
        {isUploading && (
          <div className="flex flex-col items-center gap-2">
            <Loader2 size={28} className="animate-spin text-purple-500" />
            <span className="text-sm text-gray-600">Uploading…</span>
          </div>
        )}

        {/* Preview state */}
        {imageUrl && !isUploading && (
          <>
            <img
              src={imageUrl}
              alt="preview"
              className="h-48 w-full object-contain rounded-lg"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onFileChange(undefined);
              }}
              className="absolute top-3 right-3 p-1.5 rounded-full bg-white/90 
                         hover:bg-red-50 shadow-md transition-colors"
              aria-label="Remove image"
            >
              <X size={16} className="text-[#c00035]" />
            </button>
            <p className="mt-3 text-xs text-gray-500">
              Click or drag a new image to replace
            </p>
          </>
        )}
      </div>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-[#c00035]"
        >
          {error}
        </motion.p>
      )}
    </div>
  );
};

export default ImageUpload;

